import base64 from 'base-64';
import { jwtDecode } from 'jwt-decode';
import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { PermissionType } from '../../constants/enums/permission-type-enum';
import userTypeEnum from '../../constants/enums/user-type-enum';
import URLConstants from '../../constants/url-constants';
import ILogin from '../../models/forms/ILogin';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import IAuthResponse from '../../models/response/IAuthResponse';
import { IRoleVM } from '../../models/response/IRoleResponse';
import { IAuthState } from '../../models/state/IAuthState';
import { ICommonState } from '../../models/state/ICommonState';
import { formatMessage } from '../../translations/format-message';
import { ObservableInitialState } from '../initialState';
import * as baseService from '../service/base-service';

export class AuthStore implements IAuthState, ICommonState {
  initialStateValue = ObservableInitialState;
  getAPIVersionServiceState = { ...this.initialStateValue };
  inProgress = false;
  error = '';
  isAuthenticated = false;
  token = '';
  userId = 0;
  tenantId = 0;
  userType = '';
  email = '';
  loginFormData: ILogin = {
    UserId: '',
    Password: '',
    RememberMe: false,
  };
  logoutSuccess = false;
  logoutError = '';
  logoutInprogress = false;

  permissions: Array<IRoleVM> = [];

  clearCacheState = {...this.initialStateValue};

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      tenantId: observable,
      userId: observable,
      isAuthenticated: observable,
      loginFormData: observable,
      logoutSuccess: observable,
      logoutError: observable,
      logoutInprogress: observable,
      permissions: observable,
      clearCacheState: observable,
      ClearCacheService: action,
      reset: action,
      login: action,
      logout: action,
      getUserId: computed,
      getPermissionType: computed,
      getAllPermission: computed,
      resetStore: action,
      resetClearCacheState: action,
    });
  }

  ClearCacheService = () => {
    this.clearCacheState.inProgress = true;
    const url = URLConstants.ClearCache;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response?.data?.Error) {
          this.clearCacheState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.clearCacheState.success = true;
        }
      })
      .catch((err: string) => {
        this.clearCacheState.error = err;
      })
      .finally(
        action(() => {
          this.clearCacheState.inProgress = false;
        })
      );
  };

  get getUserType() {
    if (this.userType) {
      return this.userType;
    }
    return localStorage.getItem('oms_userType');
  }

  get getPermissionType() {
    return Number(localStorage.getItem('oms_permission_type'));
  }

  get getToken() {
    return localStorage.getItem('oms_token');
  }

  get getRefreshToken() {
    return localStorage.getItem('oms_refreshToken');
  }

  get getEmail() {
    return localStorage.getItem('oms_email');
  }

  get getUserName(){
    return localStorage.getItem('oms_userName')
  }

  get getUserId() {
    let userId = localStorage.getItem('oms_userId')
      ? localStorage.getItem('oms_userId')
      : '0';
    return parseInt(userId!);
  }

  get getAllPermission() {
    if (this.permissions) {
      return [...this.permissions];
    }
    return [];
  }

  resetClearCacheState = () => {
    this.clearCacheState = { ...this.initialStateValue };
  }

  /*
    This function is used to reset all observables to their initial values.
    */

  reset = () => {
    this.error = '';
    this.inProgress = false;
    this.isAuthenticated = false;
    this.logoutError = '';
    this.logoutInprogress = false;
    this.logoutSuccess = false;
    this.loginFormData = {
      UserId: '',
      Password: '',
      RememberMe: false,
    };
  };
  /*
    This function is used to reset Logout observables to their initial values.
    */

  resetLogout = () => {
    this.logoutError = '';
    this.logoutInprogress = false;
    this.logoutSuccess = false;
  };

  /*
    This function is used to reset all store observables to their initial values.
    */

  resetStore = () => {
    this.inProgress = false;
    this.error = '';
    this.tenantId = 0;
    this.userId = 0;
    this.isAuthenticated = false;
    this.loginFormData = {
      UserId: '',
      Password: '',
      RememberMe: false,
    };
    this.logoutSuccess = false;
    this.logoutError = '';
    this.logoutInprogress = false;
    this.permissions = [];
  };

  /*
    This function is used to Authenticate User and get token (if authentic) from API.
    */

  login = (data: ILogin) => {
    this.inProgress = true;
    this.error = '';
    this.loginFormData = JSON.parse(JSON.stringify(data));
    const authData = {
      UserId: data.UserId,
      Password: base64.encode(data.Password),
    };
    // ClearCache replies with "Clear-Site-Data: storage", which wipes localStorage
    // once the browser trusts the origin. Run it before authenticating, never after.
    return this.ClearCacheService()
      .then(() => baseService.postRequest(URLConstants.Authenticate, authData))
      .then((response: IApiResponse<IApiSuccessResponse<IAuthResponse>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
        } else {
          let data = response.data.Data;
          let permissions: Array<IRoleVM> = data.Role ? data.Role : [];
          this.permissions = permissions;
          const userToken: any = jwtDecode(data.AccessToken);
          this.userType = userTypeEnum[userToken.userType];
          localStorage.setItem('oms_email', userToken.userEmail);
          localStorage.setItem('oms_userName', userToken.userName)
          localStorage.setItem('oms_userId', data.UserId);
          localStorage.setItem(
            'oms_userType',
            userTypeEnum[userToken.userType]
          );
          localStorage.setItem('oms_token', data.AccessToken);
          localStorage.setItem(
            'oms_permission_type',
            PermissionType[userToken.permissionType]
          );
          localStorage.setItem('oms_is_access_tenant', 'false');
          localStorage.setItem('oms_access_tenant_id', userToken.tenantId);
          localStorage.setItem('oms_access_tenant_name', '');
          localStorage.setItem('oms_permissions', JSON.stringify(permissions));
          this.isAuthenticated = true;
          toast.success(formatMessage('login_successfully'));
        }
      })
      .catch((err: string) => {
        this.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /*
    This function is used to reset all observables to their initial values and clear local storage.
    */

  logout = () => {
    this.logoutInprogress = true;
    return baseService
      .postRequest(URLConstants.Logout, {})
      .then((response: IApiResponse<any>) => {
        if (response.data.Error) {
          this.logoutError = response.data.Message;
        } else {
          this.reset();
          const lang: any = localStorage.getItem('oms_language');
          const processOrder: string = localStorage.getItem('process_order');
          localStorage.clear();
          localStorage.setItem('oms_language', lang === 'en' ? 'en' : 'jp');
          localStorage.setItem('oms_is_access_tenant', 'false');
          localStorage.setItem('oms_access_tenant_id', '0');
          localStorage.setItem('oms_access_tenant_name', '');
          localStorage.setItem('process_order', processOrder);
          localStorage.setItem(
            'process_order',
            processOrder === 'null' ? '' : processOrder
          );
          this.logoutSuccess = true;
        }
      })
      .catch((err: string) => {
        this.logoutError = err;
      })
      .finally(
        action(() => {
          this.logoutInprogress = false;
        })
      );
  };
}

export default new AuthStore();
